// Which rail row lights for a path — extracted from cos-app-shell's
// `_isActive`/`_isActiveLink` so the breadcrumb trail can ask the SAME
// question the rail asks.
//
// The trail's first crumb must be the row the rail is highlighting
// (docs/design-system/breadcrumb-trail.md §1, "treatment 5, section
// anchored"): on /billing/contracts/2202 the rail lights "Contracts", so the
// trail must start "Contracts", not "Billing". Two implementations of the
// match would drift the moment one of them grew a special case — and they
// already had three between them (the /dashboard root alias, the /billing
// exact-match arm, the per-row activeMatch overrides) — so there is exactly
// one, here, and lint/trail-registry-drift.test.ts asserts every registry
// entry agrees with it.

/** The shape both the rail and this matcher need: a nav row's href plus the
 *  optional matcher a row carries when two rows split one path prefix
 *  (Nurture's Audience and Marketing both live under /nurture). */
export interface RailItem {
	href: string;
	label: string;
	icon: string;
	activeMatch?: (path: string) => boolean;
}

/** True when the row at `href` is the current one for `path`. Behaviour is
 *  cos-app-shell's `_isActive` verbatim; the comments are its own. */
export function railHrefMatches(href: string, path: string): boolean {
	// Nav hrefs may carry a view query (the partner-side KB entry);
	// `path` is pathname-only, so compare against the path part.
	const base = href.split('?')[0];
	if (base === '/dashboard') return path === '/dashboard' || path === '/';
	// A SECTION HOME WHOSE HREF IS A PREFIX OF ITS SIBLINGS MATCHES
	// EXACTLY. The default below is a prefix match, which is right for
	// /tickets lighting up on /tickets/42 — but /billing is the parent of
	// /billing/contracts, /billing/renewals and five more, so a prefix
	// match lights the home entry on every page in the cluster and two
	// items read as current at once. /billing is currently the only nav
	// href that is a prefix of another; add to this check if a second
	// section grows a home.
	if (base === '/billing') return path === '/billing';
	// Partner-scoped "My Library" link points at /partners/:id; the
	// active state should still light up when the user is on it.
	if (base.startsWith('/partners/')) return path === base;
	return path.startsWith(base);
}

/** Active state for a nav ROW, which may carry its own matcher. */
export function railItemMatches(item: RailItem, path: string): boolean {
	return item.activeMatch ? item.activeMatch(path) : railHrefMatches(item.href, path);
}

/** The row the rail lights for `path`, or null when none does (the header
 *  chrome destinations, and a handful of drill-through pages that live under
 *  no rail row at all). First match wins, which is the rail's own behaviour:
 *  it renders rows in order and each decides for itself. */
export function activeRailItemFor(path: string, items: readonly RailItem[]): RailItem | null {
	return items.find((item) => railItemMatches(item, path)) ?? null;
}
